import React, { useState } from 'react';
import * as monaco from 'monaco-editor';
import MonacoEditorWithFullscreen from 'components/Common/MonacoEditorWithFullscreen';
import { useTheme } from 'contexts/ThemeContext';

// Define custom theme for the editor
monaco.editor.defineTheme("request-theme", {
    base: 'vs',
    inherit: true,
    rules: [
        // Method colors matching getMethodBadgeStyle
        { token: 'get', foreground: '047857' },      // text-green-800
        { token: 'post', foreground: '92400E' },     // text-amber-800
        { token: 'put', foreground: '1E40AF' },      // text-blue-800
        { token: 'delete', foreground: '991B1B' },   // text-red-800
        { token: 'patch', foreground: '6D28D9' },    // text-violet-800
        { token: 'head', foreground: '047857' },     // text-green-800
        { token: 'options', foreground: '7E22CE' },  // text-purple-800
        { token: 'default-method', foreground: '1F2937' }, // text-gray-800
        // Other syntax elements
        { token: 'header', foreground: 'A31515' },      // Red for headers
        { token: 'value', foreground: '0451A5' },       // Blue for values
        { token: 'delimiter', foreground: 'A31515' },   // Red for brackets
        { token: 'key', foreground: 'A31515' },         // Red for JSON keys
    ],
    colors: {
        'editor.background': '#ffffff',             // Light mode background
        'editor.foreground': '#282a2c',             // Light mode text
        'editorLineNumber.foreground': '#64748B',   // Light mode line numbers
        'editor.lineHighlightBackground': 'rgba(0, 0, 0, 0.04)',
        'editor.lineHighlightBorder': 'rgba(0, 0, 0, 0.04)',
        'editorGutter.background': '#e9e9e9',       // Light mode gutter
    }
});

// Define dark theme for the editor
monaco.editor.defineTheme("request-theme-dark", {
    base: 'vs-dark',
    inherit: true,
    rules: [
        // Method colors matching getMethodBadgeStyle - brighter for dark mode
        { token: 'get', foreground: '4ade80' },      // brighter green
        { token: 'post', foreground: 'fbbf24' },     // brighter amber
        { token: 'put', foreground: '60a5fa' },      // brighter blue
        { token: 'delete', foreground: 'f87171' },   // brighter red
        { token: 'patch', foreground: 'a78bfa' },    // brighter violet
        { token: 'head', foreground: '4ade80' },     // brighter green
        { token: 'options', foreground: 'c084fc' },  // brighter purple
        { token: 'default-method', foreground: 'e2e8f0' }, // light gray
        // Other syntax elements - brighter for dark mode
        { token: 'header', foreground: 'f87171' },      // brighter red for headers
        { token: 'value', foreground: '60a5fa' },       // brighter blue for values
        { token: 'delimiter', foreground: 'f87171' },   // brighter red for brackets
        { token: 'key', foreground: 'f87171' },         // brighter red for JSON keys
    ],
    colors: {
        'editor.background': '#0e1525',             // Your dark theme background
        'editor.foreground': '#d4d4d4',             // VS Code light gray text
        'editorLineNumber.foreground': '#94a3b8',   // Your dark theme line numbers
        'editor.lineHighlightBackground': 'rgba(255, 255, 255, 0.04)',
        'editor.lineHighlightBorder': 'rgba(255, 255, 255, 0.04)',
        'editorGutter.background': '#0e1525',       // Your dark theme gutter
        'editor.selectionBackground': '#264f78',     // VS Code selection
        'editor.inactiveSelectionBackground': '#3a3d41', // VS Code inactive selection
        'editor.wordHighlightBackground': '#575757', // VS Code word highlight
        'editor.findMatchBackground': '#515c6a',     // VS Code find match
        'editor.findMatchHighlightBackground': '#515c6a', // VS Code find match highlight
    }
});

// Register a custom language for request highlighting
monaco.languages.register({ id: 'request-language' });
monaco.languages.setMonarchTokensProvider('request-language', {
    tokenizer: {
        root: [
            // HTTP Methods with specific colors
            [/\b(GET)\b/, 'get'],
            [/\b(POST)\b/, 'post'],
            [/\b(PUT)\b/, 'put'],
            [/\b(DELETE)\b/, 'delete'],
            [/\b(PATCH)\b/, 'patch'],
            [/\b(HEAD)\b/, 'head'],
            [/\b(OPTIONS)\b/, 'options'],
            [/\b(TRACE|CONNECT)\b/, 'default-method'],
            // Headers
            [/^[A-Za-z-]+:/, 'header'],
            // Header values
            [/:(.*)$/, 'value'],
            // JSON keys
            [/"([^"]*)"(?=\s*:)/, 'key'],
            // Brackets and braces
            [/[{}\[\],]/, 'delimiter'],
        ]
    }
});

const CurlDetails = ({ curlData }) => {
    const { isDarkMode } = useTheme();
    if (!curlData) return null;
    // Format the request data into HTTP format
    const formatRequest = () => {
        const lines = [];
        lines.push(`${curlData.curl}`);
        lines.push('');
        return lines.join('\n');
    };

    return (
        <div className="space-y-4">
            <div className="bgPrimary rounded-lg border !border-[var(--borderColor)] shadow-sm hover:shadow-md transition-shadow duration-200">
                <div className="px-4 py-2 flex justify-between items-center" style={{ borderBottom: '2px solid var(--borderColor)' }}>
                    <span className="!text-xl text-[var(--text-secondary)]">CURL</span>
                </div>
                <MonacoEditorWithFullscreen
                    language="request-language"
                    theme={isDarkMode ? "request-theme-dark" : "request-theme"}
                    value={formatRequest()}
                    height="calc(100vh - 300px)"
                    options={{
                        readOnly: true,
                        minimap: { enabled: false },
                        lineNumbers: 'on',
                        renderLineHighlight: 'none',
                        scrollBeyondLastLine: false,
                        fontSize: 12,
                        fontFamily: 'Monaco, monospace',
                        wordWrap: 'on',
                        automaticLayout: true,
                        scrollbar: {
                            vertical: 'visible',
                            horizontal: 'visible',
                            verticalScrollbarSize: 14,
                            horizontalScrollbarSize: 14,
                            verticalSliderSize: 14,
                            horizontalSliderSize: 14,
                        },
                        overviewRulerBorder: false,
                        hideCursorInOverviewRuler: true,
                    }}
                />
            </div>
        </div>
    );
};

export default CurlDetails;